// ══════════════════════════════════════════════════════════════════════
// PDF Helper Functions
// ══════════════════════════════════════════════════════════════════════

export function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
}

export function numberToWords(num) {
  if (num === 0) return 'Zero';
  const a = [
    '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
    'Seventeen', 'Eighteen', 'Nineteen',
  ];
  const b = [
    '', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy',
    'Eighty', 'Ninety',
  ];

  const numStr = Math.floor(Math.abs(num)).toString();
  if (numStr.length > 9) return 'Amount too large';

  const padded = ('000000000' + numStr).slice(-9);
  const parts = [
    padded.substring(0, 2),
    padded.substring(2, 4),
    padded.substring(4, 6),
    padded.substring(6, 7),
    padded.substring(7, 9),
  ];

  let words = '';
  const twoDigitToWords = (n) => {
    const val = parseInt(n, 10);
    if (val === 0) return '';
    if (val < 20) return a[val];
    return b[Math.floor(val / 10)] + (val % 10 ? ' ' + a[val % 10] : '');
  };

  if (parseInt(parts[0], 10) > 0) words += twoDigitToWords(parts[0]) + ' Crore ';
  if (parseInt(parts[1], 10) > 0) words += twoDigitToWords(parts[1]) + ' Lakh ';
  if (parseInt(parts[2], 10) > 0) words += twoDigitToWords(parts[2]) + ' Thousand ';
  if (parseInt(parts[3], 10) > 0) words += a[parseInt(parts[3], 10)] + ' Hundred ';
  const tensOnes = parseInt(parts[4], 10);
  if (tensOnes > 0) {
    if (words.length > 0) words += 'and ';
    words += twoDigitToWords(parts[4]);
  }

  const decimal = Math.round((Math.abs(num) % 1) * 100);
  if (decimal > 0) {
    words += ' and Cent ' + twoDigitToWords(String(decimal).padStart(2, '0'));
  }

  return words.trim() + ' Only';
}

export function sv(val, fallback = '') {
  return val !== null && val !== undefined && val !== '' ? String(val) : fallback;
}

// Amount in words reaches us in several shapes: the backend writes
// "US Dollar Six Thousand Only" on save, the invoice form writes
// "USD Six Thousand Only", and older rows still carry a trailing full stop.
// Strip the currency prefix and the trailing "Only"/"." so callers can add
// exactly the wording their document needs without doubling it up.
export function amountWordsCore(words) {
  return sv(words)
    .replace(/^(us\s*dollars?|usd)\.?\s*/i, '')
    .replace(/[\s.]*only[\s.]*$/i, '')
    .replace(/\.+$/, '')
    .trim();
}

// Canonical spelling for every document: "US Dollar <words> Only" — one
// "Only", no full stop after it.
export function amountInWords(document, totalUsd) {
  const core = amountWordsCore(sv(document.total_in_words, numberToWords(totalUsd)));
  return core ? `US Dollar ${core} Only` : '';
}

export function getItems(document) {
  return document.lineItems || document.line_items || [];
}

export function getTrBoe(document) {
  return document.trBoe || document.tr_boe || {};
}

// Packages print against one unit on every document — the counts of the line
// items added up and typed as "8 PKGS", whatever each row is packed in. The
// invoice may name that unit itself ("8 DRUMS"); this is what it falls back to.
export const PKG_UNIT = 'PKGS';

// The number of packages the invoice ships: the document total when it carries
// one, otherwise the line items added up.
export function totalPackages(document = {}, items = []) {
  return parseInt(document.total_packages, 10) ||
    items.reduce((sum, item) => sum + (parseInt(item.no_of_packages, 10) || 0), 0);
}

// The packages total as it prints — "8 PKGS" — empty when nothing is packed,
// so the caller can leave the line out or fall back to its own wording.
export function buildPackagesLine(document = {}, items = []) {
  const total = totalPackages(document, items);
  const unit = sv(document.total_package_unit).trim().toUpperCase() || PKG_UNIT;
  return total > 0 ? `${total} ${unit}` : '';
}

// Buyer registration details (IRC NO., VAT etc.) — printed on their own row,
// separate from the buyer address. Prefers the per-document snapshot, falls
// back to the saved buyer record.
export function getBuyerDetailsText(document) {
  const bd = document.buyerDetail || document.buyer_detail || {};
  return (sv(document.buyer_details) || sv(bd.details)).trim();
}

// ── Insurance cover note ─────────────────────────────────────────────────
// Builds the insurance cover-note line, e.g.
// "OPENER'S COVER NOTE NO.DGIC/KUSH/MC- DT.06.08.2026 OF X INSURANCE COMPANY LIMITED".
// "OPENER'S" (and "COVER NOTE NO." when the ref is a bare number) is prefixed only
// when the entered reference doesn't already carry it.
export function buildInsuranceLine(document) {
  const company = sv(document.insurance_company).trim().toUpperCase();
  let ref = sv(document.insurance_ref).trim();
  if (!ref) return company;
  if (!/^opener['’]?s?\b/i.test(ref)) {
    ref = /^cover\s*note\b/i.test(ref) ? `OPENER'S ${ref}` : `OPENER'S COVER NOTE NO. ${ref}`;
  }
  return company ? `${ref} OF ${company}` : ref;
}

// The importer's registration line inside a verbatim text block. What the line
// opens with is not fixed - "LCA NO.", "IRC NO.", "PREVIOUS IRC NO.", a note of
// its own - so it is recognised by the numbers it carries rather than by how it
// begins, anywhere along the line. Requiring "NO" behind the label keeps the
// invoice wording around it out: "LUT ARN NO.", "PROFORMA INVOICE NO." and the
// rest name no registration of the importer's.
const REGISTRATION_LEAD = /\b(LCA|IRC|BIN|TIN|VAT)\b[\s./-]*(REG\.?[\s.]*)?NO\b/i;

// Inserts the insurance cover-note line just above the registration line of a
// verbatim text block. Returns null when the block has no such line.
function insertAboveRegistration(text, insLine) {
  if (!text || !insLine) return null;
  const lines = text.split('\n');
  const regIdx = lines.findIndex(l => REGISTRATION_LEAD.test(l));
  if (regIdx < 0) return null;
  lines.splice(regIdx, 0, insLine);
  return lines.join('\n');
}

// Places the insurance cover-note line into the two verbatim blocks an invoice
// prints — Declaration and Other Reference. It belongs immediately above the
// registration line of whichever block carries one; with no registration line
// anywhere it is appended to the reference block so the cover note still prints.
// Returns both blocks as they should be printed (trimmed, line breaks kept).
export function placeInsuranceLine(declaration, otherRef, insLine) {
  const decl = sv(declaration).trim();
  const ref = sv(otherRef).trim();
  if (!insLine || decl.includes(insLine) || ref.includes(insLine)) {
    return { declaration: decl, otherRef: ref };
  }
  const declWithIns = insertAboveRegistration(decl, insLine);
  if (declWithIns) return { declaration: declWithIns, otherRef: ref };

  const refWithIns = insertAboveRegistration(ref, insLine);
  if (refWithIns) return { declaration: decl, otherRef: refWithIns };

  return { declaration: decl, otherRef: ref ? ref + '\n' + insLine : insLine };
}

// ── Buyer registration line ────────────────────────────────────────────────
// The importer's registration block — "LCA NO.… IRC NO.… BIN NO.… TIN NO.…
// AND LC ISSUING BANK'S BIN NO.…" — is typed as a single line inside
// Declaration or Other Reference, below the LUT/certification paragraphs.
// An importer holding no licence has no LCA to lead with and starts at
// "PREVIOUS IRC NO." or "IRC NO.", so the line is taken whichever label opens
// it. Returns that one line; the paragraphs around it are invoice wording and
// have no place on a consignment note.
export function getRegistrationLine(document) {
  return findRegistrationLine(document);
}

// Other Reference 2 is the box kept for this line alone, so whatever stands in
// it is quoted as typed - nothing to match and nothing to guess, under whatever
// label the numbers were written. Only when that box is empty is the line
// hunted for in the invoice wording, the way invoices raised before the box
// existed still carry it. The whole line comes back exactly as it was typed,
// whatever stands in front of the numbers, and Declaration is read before
// Other Reference, so an invoice carrying the block in both prints the
// declaration's copy.
function findRegistrationLine(document) {
  const typed = sv(document.other_ref_2).trim();
  if (typed) return typed;

  const blocks = [sv(document.declaration), sv(document.other_ref)];
  for (const block of blocks) {
    const line = block.split('\n').find(l => REGISTRATION_LEAD.test(l));
    if (line) return line.trim();
  }
  return '';
}

// Other Reference 2 carries the registration line on its own, so the blocks
// that print Other Reference verbatim take it as their last line - under the
// cover note, exactly where an invoice that typed it inline used to keep it.
// A block already carrying the line is left alone rather than printing it twice.
export function appendRegistrationLine(text, document) {
  const line = sv(document.other_ref_2).trim();
  if (!line) return text;
  const body = sv(text).replace(/\s+$/, '');
  if (body.includes(line)) return body;
  return body ? `${body}\n${line}` : line;
}

// ── L/C reference line ───────────────────────────────────────────────────
// "L/C NO. 109025010653 DATE : 07.10.2025, AMENDMENT NO.01 DT.27.01.2026
//  ISSUED BY AL-ARAFAH ISLAMI BANK LTD., KAWRAN BAZAR BRANCH, DHAKA".
// The amendment and the issuing bank are dropped when the invoice carries
// neither, so no document prints a dangling "ISSUED BY". Letters that spell
// the credit out in full pass their own wording, e.g.
// { label: 'LETTER OF CREDIT NO.', dateLabel: 'DATE:' }; a certificate whose
// sentence already says "ISSUED UNDER" names the bank with
// { bankLabel: 'OF', separator: ' ' } and punctuates the line itself.
export function buildLcRefLine(
  document,
  {
    label = 'L/C NO.', dateLabel = 'DATE :', bankLabel = 'ISSUED BY', separator = ', ',
  } = {},
) {
  const lcNo = sv(document.lc_no).trim();
  if (!lcNo) return '';

  let head = `${label} ${lcNo}`;
  if (document.lc_opening_date) head += ` ${dateLabel} ${formatDate(document.lc_opening_date)}`;

  const tail = [];
  const amendment = sv(document.lc_amendment_ref).trim();
  if (amendment) {
    tail.push(`AMENDMENT NO.${amendment}` +
      (document.lc_amendment_ref_date ? ` DT.${formatDate(document.lc_amendment_ref_date)}` : ''));
  }
  const bank = [sv(document.lc_opening_bank).trim(), sv(document.lc_opening_bank_address).trim()]
    .filter(Boolean).join(', ').toUpperCase();
  if (bank) tail.push(`${bankLabel} ${bank}`);

  return tail.length ? `${head}${separator}${tail.join(' ')}` : `${head},`;
}

// ── Despatch reference ───────────────────────────────────────────────────
// "233950 DATE : 31.01.2026 OF JAIN PARIVAHAN 1, JADULAL MULLICK ROAD,
// KOLKATA-700006, INDIA" — the consignment note the goods travelled under and
// the transporter that issued it. Empty when no despatch document was
// entered, so a certificate drops the part instead of printing a bare
// "DATE :" followed by whichever address happens to be at hand.
export function buildDespatchRef(trBoe = {}) {
  // The consignment note number is what the rest of the line is about — with
  // no number there is nothing to date or to attribute to a transporter.
  const no = sv(trBoe.despatch_doc_no).trim();
  if (!no) return '';

  const head = trBoe.despatch_date ? `${no} DATE : ${formatDate(trBoe.despatch_date)}` : no;
  const carrier = sv(trBoe.transporter_name || trBoe.carrier).trim().toUpperCase();
  return carrier ? `${head} OF ${carrier}` : head;
}

// ── Linked proforma invoice ──────────────────────────────────────────────
// A commercial invoice has no proforma_invoice_no column — the proforma
// reaches the PDF through the preloaded `proforma` association.
export function getProforma(document) {
  return document.proforma || document.proforma_invoice || {};
}

// "PROFORMA INVOICE NO.005/PI/KCIPL/25-26 DATED: 29.09.2025". Empty when no
// proforma is linked so the caller drops the part instead of printing a
// bare label, and the label is not re-added when the number already has it.
export function buildProformaRef(document) {
  const proforma = getProforma(document);
  const no = sv(proforma.invoice_no || document.proforma_invoice_no).trim();
  if (!no) return '';
  const date = proforma.document_date || document.proforma_invoice_date;
  const label = /^proforma\b/i.test(no) ? '' : 'PROFORMA INVOICE NO.';
  return `${label}${no.toUpperCase()}${date ? ` DATED: ${formatDate(date)}` : ''}`;
}

// ── Importer registration numbers ────────────────────────────────────────
// "IRC NO.260326111525520, E-TIN NO.156583769627, BIN NO.000865146-0402 AND
// LC ISSUING BANK'S BIN NO.000096915-0208". These are the buyer's numbers,
// normally typed as one line inside Declaration / Other Reference; the
// company columns are only a fallback. Labels with nothing behind them are
// left out entirely.
export function buildRegistrationRef(document, company = {}) {
  const parts = [];
  const typed = findRegistrationLine(document);
  if (typed) {
    parts.push(typed);
  } else {
    if (sv(company.irc_no).trim()) parts.push(`IRC NO.${sv(company.irc_no).trim()}`);
    if (sv(company.tin_no).trim()) parts.push(`E-TIN NO.${sv(company.tin_no).trim()}`);
    if (sv(company.vat_no).trim()) parts.push(`BIN NO.${sv(company.vat_no).trim()}`);
  }

  const text = parts.join(', ');
  const bin = sv(document.bank_bin).trim();
  if (!bin || text.includes(bin)) return text;
  const issuing = `LC ISSUING BANK'S BIN NO.${bin}`;
  return text ? `${text} AND ${issuing}` : issuing;
}

// ── Insurance cover note, as a reference line ────────────────────────────
// "COVER NOTE NO.0171/10/2025 DATED: 05/10/2025 OF TAKAFUL ISLAMI INSURANCE
// LIMITED". insurance_ref is typed free-hand and usually already leads with
// "COVER NOTE NO." and carries its own date — neither may be re-added.
const COVER_NOTE_DATE = /\b(DT|DTD|DATED?)\b\.?\s*\d/i;

export function buildCoverNoteRefLine(document) {
  const ref = sv(document.insurance_ref).trim();
  if (!ref) return '';

  let line = /^cover\s*note\b/i.test(ref) ? ref : `COVER NOTE NO.${ref}`;
  if (!COVER_NOTE_DATE.test(ref) && document.document_date) {
    line += `, DATED ${formatDate(document.document_date)}`;
  }
  const insurer = sv(document.insurance_company).trim().toUpperCase();
  return insurer ? `${line} OF ${insurer}` : line;
}

// ── Truck Receipt (lorry receipt) print data ─────────────────────────────
// Builds the data printed onto the transporter's pre-printed consignment
// note (JAIN PARIVAHAN format). If the user saved an edited truck receipt
// for the document, the saved values are used as-is; otherwise sensible
// defaults are derived from the invoice. The same object prefills the
// Truck Receipt edit form, so what you see there is what prints.
export function buildTruckReceiptData(document, company = {}) {
  const trBoe = getTrBoe(document);
  const items = getItems(document);

  const netWt = parseFloat(document.total_net_weight) ||
    items.reduce((s, i) => s + (parseFloat(i.net_weight_kg) || 0), 0);
  const grossWt = parseFloat(document.total_gross_weight) ||
    items.reduce((s, i) => s + (parseFloat(i.gross_weight_kg) || 0), 0);
  const totalUsd = parseFloat(document.total_usd || 0);
  const usd = (v) => `US$${parseFloat(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const commodity = items[0]
    ? sv(items[0].description_of_goods).toUpperCase()
    : sv(document.commodity).toUpperCase();
  const quantity = items[0]
    ? `${parseFloat(items[0].quantity || 0).toFixed(4)} ${sv(items[0].uom).toUpperCase()}`
    : '';

  // The reference block mirrors the invoice: linked proforma, insurance cover
  // note, then the importer's registration line. Every part is read from the
  // field that actually holds it — Other Reference carries the LUT and
  // certification wording, so it is never pasted in whole.
  const descParts = [];
  if (commodity) descParts.push(quantity ? `${commodity} QUANTITY ${quantity}` : commodity);
  if (document.fob_value_usd) descParts.push(`FOB VALUE - ${usd(document.fob_value_usd)}`);
  if (document.freight_charges_usd) descParts.push(`FREIGHT CHARGES - ${usd(document.freight_charges_usd)}`);
  if (document.hs_code) descParts.push(`H.S. CODE ${document.hs_code}`);

  const proforma = document.proforma || document.proforma_invoice || {};
  if (proforma.invoice_no) {
    descParts.push(`PROFORMA INVOICE NO.${sv(proforma.invoice_no)}` +
      (proforma.document_date ? ` DT.${formatDate(proforma.document_date)}` : ''));
  }

  const insLine = buildInsuranceLine(document);
  if (insLine) descParts.push(insLine);

  const regLine = getRegistrationLine(document);
  if (regLine) {
    descParts.push(regLine);
    // The typed line normally ends with the issuing bank's BIN already.
    if (document.bank_bin && !regLine.includes(document.bank_bin)) {
      descParts.push(`AND LC ISSUING BANK'S BIN NO.${document.bank_bin}`);
    }
  } else {
    if (company.irc_no) descParts.push(`IRC NO.${company.irc_no}`);
    if (company.vat_no) descParts.push(`BIN NO.${company.vat_no}`);
    if (company.tin_no) descParts.push(`TIN NO.${company.tin_no}`);
    if (document.bank_bin) descParts.push(`AND LC ISSUING BANK'S BIN NO.${document.bank_bin}`);
  }

  if (document.terms_of_delivery) {
    const terms = sv(document.terms_of_delivery).trim().toUpperCase();
    const dest = sv(document.shipment_to).trim().toUpperCase();
    // Terms of delivery usually spell the destination out already
    // ("CPT BENAPOLE, BANGLADESH (INCOTERMS 2010)") — appending the shipment
    // destination on top of that prints the place twice.
    const destHead = dest.split(/[\s,]+/)[0];
    descParts.push(destHead && !terms.includes(destHead) ? `${terms} ${dest}` : terms);
  }
  if (document.lc_no) descParts.push(`L/C NO. ${document.lc_no} DATE ${formatDate(document.lc_opening_date)}`);
  if (document.lc_amendment_ref) {
    descParts.push(`AMENDMENT NO.${document.lc_amendment_ref}${document.lc_amendment_ref_date ? ` DT.${formatDate(document.lc_amendment_ref_date)}` : ''}`);
  }
  if (document.invoice_no) descParts.push(`INVOICE NO. ${document.invoice_no} DATE ${formatDate(document.document_date)}`);
  descParts.push(`COUNTRY OF ORIGIN : ${sv(document.country_of_origin, 'INDIA').toUpperCase()}`);

  const bankFull = [sv(document.lc_opening_bank), sv(document.lc_opening_bank_address)]
    .filter(Boolean).join(', ').toUpperCase();
  const buyerFull = [sv(document.buyer_name), sv(document.buyer_address)]
    .filter(Boolean).join(', ').toUpperCase();

  const packagesLine = buildPackagesLine(document, items);
  const packages = packagesLine ? `${packagesLine}.` : 'LOOSE';

  const defaults = {
    consignment_note_no: sv(trBoe.despatch_doc_no),
    consignment_date: trBoe.despatch_date || document.document_date || '',
    from_place: sv(document.shipment_from).toUpperCase(),
    to_place: sv(document.shipment_to).toUpperCase(),
    delivery_office: '',
    state: '',
    tel_no: '',
    consignor: [
      sv(company.exporter_name).toUpperCase(),
      sv(company.exporter_address),
      company.email ? `E MAIL:${company.email}` : '',
      company.gstin ? `GSTIN: ${company.gstin}` : '',
    ].filter(Boolean).join(', '),
    consignee_bank: bankFull ? `TO THE ORDER OF ${bankFull}` : '',
    notify: buyerFull ? `${buyerFull}${bankFull ? ` AND ${bankFull}` : ''}` : '',
    packages,
    description: descParts.join(', ') + '.',
    actual_weight: netWt ? `${netWt.toFixed(3)} KGS.` : '',
    charged_weight: grossWt ? `${grossWt.toFixed(3)} KGS.` : '',
    value: totalUsd ? `USD : ${totalUsd.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '',
    insured: false,
    insurance_company: '',
    policy_no: '',
    policy_date: '',
    insurance_amount: '',
    risk: '',
    demurrage_days: '7',
    demurrage_rate: '20',
  };

  const saved = document.truckReceipt || document.truck_receipt;
  if (saved && saved.id) {
    // Saved values win, but fields the saved record doesn't have (e.g. a
    // receipt saved before a field existed) fall back to the derived value.
    const pick = (key) => (sv(saved[key]) !== '' ? sv(saved[key]) : defaults[key]);
    return {
      consignment_note_no: pick('consignment_note_no'),
      consignment_date: saved.consignment_date || defaults.consignment_date,
      from_place: pick('from_place'),
      to_place: pick('to_place'),
      delivery_office: sv(saved.delivery_office),
      state: sv(saved.state),
      tel_no: sv(saved.tel_no),
      consignor: pick('consignor'),
      consignee_bank: pick('consignee_bank'),
      notify: pick('notify'),
      packages: pick('packages'),
      description: pick('description'),
      actual_weight: pick('actual_weight'),
      charged_weight: pick('charged_weight'),
      value: pick('value'),
      insured: !!saved.insured,
      insurance_company: sv(saved.insurance_company),
      policy_no: sv(saved.policy_no),
      policy_date: saved.policy_date || '',
      insurance_amount: sv(saved.insurance_amount),
      risk: sv(saved.risk),
      demurrage_days: pick('demurrage_days'),
      demurrage_rate: pick('demurrage_rate'),
    };
  }
  return defaults;
}

// ── Print-layout profiles (pre-printed form overlays) ───────────────────
// A saved layout (from the visual layout editor) adjusts a generator's GEO
// table without code changes: { ox, oy, fields: { <GEO key>: { dx, dy, hide } } }.
// ox/oy shift every field at once (printer feed drift); dx/dy move one
// field; hide skips printing that field.

// Returns GEO with a layout's per-field offsets folded in. Every
// coordinate-carrying property is shifted; widths, row counts and font
// ladders stay as authored.
export function layoutGeo(GEO, layout) {
  const fields = (layout && layout.fields) || {};
  const out = {};
  Object.keys(GEO).forEach((key) => {
    const src = GEO[key];
    const dx = (fields[key] && fields[key].dx) || 0;
    const dy = (fields[key] && fields[key].dy) || 0;
    const e = { ...src };
    if (e.x !== undefined) e.x += dx;
    if (e.y !== undefined) e.y += dy;
    if (e.cx !== undefined) e.cx += dx;
    if (e.labelY !== undefined) e.labelY += dy;
    if (e.valueY !== undefined) e.valueY += dy;
    if (e.ys) e.ys = e.ys.map((y) => y + dy);
    if (e.first) e.first = { ...e.first, x: e.first.x + dx, y: e.first.y + dy };
    if (e.rest) e.rest = { ...e.rest, x: e.rest.x + dx, ys: e.rest.ys.map((y) => y + dy) };
    out[key] = e;
  });
  return out;
}

export const layoutFieldHidden = (layout, key) =>
  !!(layout && layout.fields && layout.fields[key] && layout.fields[key].hide);

// ── Ref block layout ──────────────────────────────────────────────────────
// The letters type their "Ref :" block as one hanging paragraph: only the
// first line is pushed clear of the label, every line after it — the rest of
// the L/C wording included — starts at the block's left edge.

// Wraps text so the first line fits firstWidth and the rest fit restWidth.
// jsPDF's splitTextToSize only takes one width, so the wrap is done here.
export function wrapHangingText(doc, text, firstWidth, restWidth) {
  const words = sv(text).trim().split(/\s+/).filter(Boolean);
  const lines = [];
  let line = '';
  words.forEach((word) => {
    const candidate = line ? `${line} ${word}` : word;
    const width = lines.length === 0 ? firstWidth : restWidth;
    if (line && doc.getTextWidth(candidate) > width) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  });
  if (line) lines.push(line);
  return lines;
}

// Draws one such paragraph and returns the height it took. Every line but the
// last is justified to the right margin — jsPDF only stretches the lines
// before an array's last entry, so each one is passed with a blank line
// trailing it.
export function drawHangingParagraph(doc, text, { x, firstX, rightX, y, lineHeight = 4 }) {
  const lines = wrapHangingText(doc, text, rightX - firstX, rightX - x);
  lines.forEach((line, i) => {
    const lineX = i === 0 ? firstX : x;
    const lineY = y + i * lineHeight;
    if (i === lines.length - 1) {
      doc.text(line, lineX, lineY);
      return;
    }
    doc.text([line, ''], lineX, lineY, { align: 'justify', maxWidth: rightX - lineX });
  });
  return lines.length * lineHeight;
}

// ── Letter-spaced, underlined heading ────────────────────────────────────
// jsPDF measures a string without the charSpace it will be drawn with, so a
// centred title lands left of centre and the rule under it lands somewhere
// else again. Measure the spaced width here, draw the heading from that left
// edge, and rule under exactly the span the letters occupy.
export function drawSpacedTitle(doc, text, { cx, y, charSpace = 0, gap = 1, underline = true }) {
  const width = doc.getTextWidth(text) + Math.max(text.length - 1, 0) * charSpace;
  const x = cx - width / 2;
  doc.text(text, x, y, { charSpace });
  if (underline) doc.line(x, y + gap, x + width, y + gap);
  return width;
}

// Constants
export const LW = 0.3; // line width
export const M = 10;   // margin
export const FONT = 'helvetica';
export const MM_PER_PT = 25.4 / 72; // font sizes are points, the page is mm
